/**
 * EmptyState Component
 *
 * 예산이 설정되지 않았을 때 표시하는 컴포넌트
 */

import { useNavigate } from 'react-router-dom'

/**
 * EmptyState Props
 */
export interface EmptyStateProps {
  /** 설정 버튼 클릭 핸들러 (선택, 제공되지 않으면 기본 동작으로 예산 설정 페이지로 이동) */
  onSetupClick?: () => void
}

/**
 * 예산 빈 상태 컴포넌트
 *
 * 책임:
 * - 예산이 없을 때 안내 메시지 표시
 * - 예산 설정 유도
 * - 시각적으로 친근한 UI 제공
 * - "예산 설정하기" 버튼 클릭 시 예산 설정 페이지로 이동
 */
export function EmptyState({ onSetupClick }: EmptyStateProps) {
  const navigate = useNavigate()

  /**
   * 설정 버튼 클릭 핸들러
   * onSetupClick이 제공되면 실행, 아니면 예산 설정 페이지로 이동
   */
  const handleSetupClick = () => {
    if (onSetupClick) {
      onSetupClick()
    } else {
      // 기본 동작: 예산 설정 페이지로 이동
      navigate('/settings/budget')
    }
  }

  return (
    <div className="min-h-[400px] flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        {/* Icon */}
        <div className="mb-6">
          <span className="text-8xl">💰</span>
        </div>

        {/* Message */}
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
          아직 예산이 설정되지 않았습니다
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-8">
          카테고리별 예산을 설정하고<br />
          지출 현황을 한눈에 확인해보세요
        </p>

        {/* CTA Button */}
        <button
          onClick={handleSetupClick}
          className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 dark:bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors shadow-md hover:shadow-lg"
        >
          <span className="mr-2">⚙️</span>
          예산 설정하기
        </button>

        {/* Additional Info */}
        <div className="mt-8 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
          <p className="text-sm text-primary-800 dark:text-primary-200">
            💡 <strong>팁:</strong> 예산을 설정하면 지출을 효과적으로 관리할 수 있어요
          </p>
        </div>
      </div>
    </div>
  )
}
